import { useEffect, useState } from 'react';

export function useActiveChapter(
  chapterIds: readonly string[],
  options?: { rootMargin?: string },
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (chapterIds.length === 0) {
      setActiveId(null);
      return;
    }
    if (!('IntersectionObserver' in window)) {
      setActiveId(chapterIds[0] ?? null);
      return;
    }

    const visible = new Map<string, number>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            visible.set(e.target.id, e.intersectionRatio);
          } else {
            visible.delete(e.target.id);
          }
        });
        let bestId: string | null = null;
        let bestRatio = -1;
        visible.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestId) setActiveId(bestId);
      },
      {
        rootMargin: options?.rootMargin ?? '-30% 0px -50% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    const elements: Element[] = [];
    chapterIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        io.observe(el);
        elements.push(el);
      }
    });

    return () => {
      io.disconnect();
    };
  }, [chapterIds, options?.rootMargin]);

  return activeId;
}
